import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
} from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "@repo/ui";
import { UI } from "react-day-picker";
import type { FilterYearGridDropdownProps } from "./filterTypes";

export function FilterYearGridDropdown(props: FilterYearGridDropdownProps) {
  const {
    options,
    value,
    onChange,
    disabled,
    classNames,
    style,
    "aria-label": ariaLabel,
  } = props;

  const { t } = useTranslation("doubts");
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLSpanElement>(null);

  const selectedOption = options?.find((o) => o.value === value);

  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocMouseDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const handlePick = useCallback(
    (year: number, isDisabled: boolean) => {
      if (isDisabled || disabled) return;
      onChange?.({
        target: { value: String(year) },
      } as ChangeEvent<HTMLSelectElement>);
      setOpen(false);
    },
    [disabled, onChange],
  );

  return (
    <span
      ref={wrapRef}
      className={classNames[UI.DropdownRoot]}
      data-disabled={disabled ? true : undefined}
      style={style}
    >
      <button
        type="button"
        className={`${classNames[UI.CaptionLabel]} doubts-filter-year-trigger`}
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-haspopup="listbox"
        disabled={disabled}
        onClick={() => {
          if (!disabled) setOpen((o) => !o);
        }}
      >
        {selectedOption?.label ?? value}

        <span className="doubts-filter-year-chevron">
          <Icon name="arrowDropdownDoubts" width={7.5} height={3.75} />
        </span>
      </button>

      {open && options && options.length > 0 ? (
        <div className="doubts-filter-year-panel" role="listbox">
          <div className="doubts-filter-year-panel-header">
            {t("filter_select_date_placeholder")}
          </div>
          <div className="doubts-filter-year-grid-scroll">
            <div className="doubts-filter-year-grid">
              {options.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  role="option"
                  aria-selected={opt.value === value}
                  disabled={opt.disabled}
                  className={
                    opt.value === value
                      ? "doubts-filter-year-cell doubts-filter-year-cell--selected"
                      : "doubts-filter-year-cell"
                  }
                  onClick={() => handlePick(opt.value, opt.disabled)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      ) : null}
    </span>
  );
}
